import {
    CustomListItemId,
    PlaybookId,
    PlaybookTouchpointId,
    RegionalScope,
    RegionId,
    serializeActivityTarget,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { PrimaryButton } from "components/ui/primary-button";
import React from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { TierColorPicker } from "./tier-color-picker";
import { TierTouchpointsEditor } from "./tier-touchpoints-editor";
import { EditableTouchpoint, groupTouchpointsByTier } from "./touchpoint-cadence";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];

export const referralTierIndependenceDescription =
    "Organizations, communities, and contacts are all tiered independently.";

interface ReferralTierEditorProps {
    scope: RegionalScope;
    disabled: boolean;
}

export const ReferralTierEditor: React.FC<ReferralTierEditorProps> = props => {
    const { scope, disabled } = props;
    const trpcClient = trpc.useTRPC();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const selectedRegionId = useRegionId();
    const officeRegionId = scope.kind === "office" ? scope.regionId : null;
    const activeRegionId = officeRegionId ?? selectedRegionId;
    const [newRows, setNewRows] = React.useState<
        { key: string; tierListItemId: CustomListItemId; initial: EditableTouchpoint }[]
    >([]);
    const newRowCounter = React.useRef(0);

    const agencyTiersQuery = useQuery({
        ...trpcClient.referralTier.agencyTiers.queryOptions(),
        enabled: scope.kind === "agencyWide",
    });
    const officeTiersQuery = useQuery({
        ...trpcClient.referralTier.tiers.queryOptions({
            regionId: officeRegionId ?? ("" as RegionId),
        }),
        enabled: scope.kind === "office",
    });
    const tiersQuery = scope.kind === "agencyWide" ? agencyTiersQuery : officeTiersQuery;

    const agencyPlaybookQuery = useQuery({
        ...trpcClient.marketingPlaybook.agencyPlaybook.queryOptions({
            regionId: selectedRegionId ?? ("" as RegionId),
        }),
        enabled:
            scope.kind === "agencyWide" && marketingPlaybookEnabled && !!selectedRegionId,
    });
    const officePlaybookQuery = useQuery({
        ...trpcClient.marketingPlaybook.officePlaybook.queryOptions({
            regionId: officeRegionId ?? ("" as RegionId),
        }),
        enabled: scope.kind === "office" && marketingPlaybookEnabled,
    });
    const playbookQuery =
        scope.kind === "agencyWide" ? agencyPlaybookQuery : officePlaybookQuery;

    const onSuccess = () => {
        tiersQuery.refetch();
    };

    const onTouchpointSuccess = () => {
        playbookQuery.refetch();
    };

    const createTier = useMutation({
        ...trpcClient.referralTier.createTier.mutationOptions(),
        onSuccess,
    });
    const patchTier = useMutation({
        ...trpcClient.referralTier.patchTier.mutationOptions(),
        onSuccess,
    });
    const ensureAgencyPlaybook = useMutation(
        trpcClient.marketingPlaybook.ensureAgencyPlaybook.mutationOptions(),
    );
    const createTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.createTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });
    const updateTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.updateTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });
    const deleteTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.deleteTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });

    const tiers: Tier[] = (tiersQuery.data ?? []).filter(t => !t.isArchived);
    const touchpointsByTier = groupTouchpointsByTier(
        playbookQuery.data?.touchpoints ?? [],
    );
    const playbookReady = scope.kind === "agencyWide" || playbookQuery.isSuccess;

    const resolvePlaybookId = async (
        touchpointRegionId: RegionId,
    ): Promise<PlaybookId | null> => {
        const existing = playbookQuery.data?.playbookId;
        if (existing) {
            return existing as PlaybookId;
        }
        if (scope.kind === "office") {
            return null;
        }
        const result = await ensureAgencyPlaybook.mutateAsync({
            regionId: touchpointRegionId,
        });
        return result.playbookId as PlaybookId;
    };

    const renameTier = (itemId: CustomListItemId, name: string) => {
        handleEvent("referral-tier-update", { field: "name" });
        patchTier.mutate({
            scope,
            itemId,
            name,
            color: null,
            order: null,
            isArchived: null,
        });
    };

    const recolorTier = (itemId: CustomListItemId, color: string) => {
        handleEvent("referral-tier-update", { field: "color" });
        patchTier.mutate({
            scope,
            itemId,
            name: null,
            color,
            order: null,
            isArchived: null,
        });
    };

    const archiveTier = (itemId: CustomListItemId) => {
        handleEvent("referral-tier-archive");
        patchTier.mutate({
            scope,
            itemId,
            name: null,
            color: null,
            order: null,
            isArchived: true,
        });
    };

    const addRow = (tierListItemId: CustomListItemId, initial: EditableTouchpoint) => {
        if (!activeRegionId) {
            return;
        }
        newRowCounter.current += 1;
        setNewRows(prev => [
            ...prev,
            { key: `new-${newRowCounter.current}`, tierListItemId, initial },
        ]);
    };

    const cancelNewRow = (key: string) => {
        setNewRows(prev => prev.filter(row => row.key !== key));
    };

    const createTouchpointFromRow = async (key: string, value: EditableTouchpoint) => {
        const row = newRows.find(r => r.key === key);
        if (!activeRegionId || !row || value.targets.length === 0) {
            return;
        }
        const playbookId = await resolvePlaybookId(activeRegionId);
        if (!playbookId) {
            return;
        }
        handleEvent("marketing-touchpoint-create");
        createTouchpoint.mutate(
            {
                regionId: activeRegionId,
                playbookId,
                tierListItemId: row.tierListItemId,
                targets: value.targets.map(serializeActivityTarget),
                intervalUnit: value.intervalUnit,
                intervalCount: value.intervalCount,
            },
            {
                onSuccess: () => cancelNewRow(key),
            },
        );
    };

    const saveTouchpoint = (id: string, value: EditableTouchpoint) => {
        if (!activeRegionId || value.targets.length === 0) {
            return;
        }
        handleEvent("marketing-touchpoint-update");
        updateTouchpoint.mutate({
            regionId: activeRegionId,
            id: id as PlaybookTouchpointId,
            targets: value.targets.map(serializeActivityTarget),
            intervalUnit: value.intervalUnit,
            intervalCount: value.intervalCount,
        });
    };

    const deleteTouchpointById = (id: string) => {
        if (!activeRegionId) {
            return;
        }
        handleEvent("marketing-touchpoint-delete");
        deleteTouchpoint.mutate({
            regionId: activeRegionId,
            id: id as PlaybookTouchpointId,
        });
    };

    return (
        <div className="space-y-4 max-w-2xl">
            {tiers.map(tier => {
                const itemId = tier.id as CustomListItemId;
                return (
                    <div
                        key={tier.id}
                        className="space-y-3 rounded-lg border border-gray-200 p-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[12rem]">
                                <AutosavingInput
                                    initial={tier.name}
                                    disabled={disabled}
                                    onCommit={newVal => renameTier(itemId, newVal)}
                                />
                            </div>
                            <TierColorPicker
                                name={tier.name || "Tier"}
                                color={tier.color}
                                disabled={disabled}
                                onSelect={token => recolorTier(itemId, token)}
                            />
                            <DeleteButton
                                backgroundColor="bg-white"
                                disabled={disabled}
                                onClick={() => archiveTier(itemId)}
                                confirm={{
                                    title: "Remove tier?",
                                    message: `Are you sure you want to remove the "${
                                        tier.name || "Tier"
                                    }" tier?`,
                                    confirmLabel: "Yes, remove",
                                }}
                            />
                        </div>
                        {marketingPlaybookEnabled && activeRegionId && playbookReady ? (
                            <div className="border-t border-gray-100 pt-3">
                                <TierTouchpointsEditor
                                    touchpoints={touchpointsByTier.get(tier.id) ?? []}
                                    newRows={newRows
                                        .filter(r => r.tierListItemId === tier.id)
                                        .map(r => ({ key: r.key, initial: r.initial }))}
                                    tierKey={tier.key}
                                    disabled={disabled}
                                    onAddRow={initial => addRow(itemId, initial)}
                                    onSaveTouchpoint={saveTouchpoint}
                                    onDeleteTouchpoint={deleteTouchpointById}
                                    onCreateTouchpoint={createTouchpointFromRow}
                                    onCancelNewRow={cancelNewRow}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
            <PrimaryButton
                disabled={disabled}
                onClick={() => {
                    handleEvent("referral-tier-create");
                    createTier.mutate({ scope, name: "New tier", color: "gray" });
                }}>
                Add tier
            </PrimaryButton>
        </div>
    );
};
